export function normalizeApiPayload(data) {
  if (data == null || data === "") return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

function pushIfMsg(arr, msg) {
  if (!msg) return;
  const s = String(msg).trim();
  if (!s) return;
  arr.push(s);
}

function messagesFromList(list) {
  const out = [];
  if (!Array.isArray(list)) return out;

  list.forEach((e) => {
    if (e == null) return;
    if (typeof e === "string") {
      pushIfMsg(out, e);
      return;
    }
    if (typeof e === "object") {
      // Swagger legacy: { item1, item2 }
      if ("item1" in e || "item2" in e) {
        const msg = [e.item1, e.item2].filter(Boolean).join(" — ");
        pushIfMsg(out, msg);
        return;
      }
      // Common: { message } / { Message }
      pushIfMsg(out, e.message ?? e.Message);
      return;
    }
    pushIfMsg(out, String(e));
  });

  return out;
}

function messagesFromModelStateErrors(errorsObj) {
  // ASP.NET style: { errors: { Field: ["msg1","msg2"], ... } }
  const out = [];
  if (!errorsObj || typeof errorsObj !== "object" || Array.isArray(errorsObj)) return out;
  Object.values(errorsObj).forEach((v) => {
    if (Array.isArray(v)) v.forEach((m) => pushIfMsg(out, m));
    else pushIfMsg(out, v);
  });
  return out;
}

/** Cuando el cliente o el proxy cortan por tiempo pero el backend (p. ej. COM) puede seguir trabajando */
export const MESSAGE_LONG_OPERATION_STILL_PROCESSING =
  "La solicitud superó el tiempo de espera, pero la liquidación puede seguir procesándose en el servidor. Espere unos minutos y revise el listado para confirmar el estado.";

/**
 * Timeout de axios, gateway timeout, o respuestas típicas cuando el trabajo sigue en curso.
 */
export function isRequestTimeoutLikeError(err) {
  if (!err) return false;
  const code = err.code;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") return true;

  const msg = String(err.message || "").toLowerCase();
  if (msg.includes("timeout") && (msg.includes("exceed") || msg.includes("agot"))) return true;

  const status = err.response?.status;
  if (status === 408 || status === 502 || status === 503 || status === 504 || status === 524) return true;

  return false;
}

function stringBodyToUserMessages(s) {
  const out = [];
  const trimmed = String(s).trim();
  if (!trimmed) return out;

  // Respuestas text/html o páginas de error de IIS/ASP.NET/nginx que axios entrega como string
  const looksLikeHtml =
    /^<!DOCTYPE\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed);
  if (looksLikeHtml) {
    pushIfMsg(
      out,
      "El servidor devolvió una página HTML en lugar de un mensaje de error. Suele indicar un fallo en el backend, un error de proxy o que el servicio no está disponible."
    );
    return out;
  }

  if (trimmed.length <= 1000) pushIfMsg(out, trimmed);
  else pushIfMsg(out, `${trimmed.slice(0, 400)}… (mensaje recortado)`);
  return out;
}

export function extractApiErrorMessages(err) {
  const dataRaw = err?.response ? err.response.data : err;
  const parsed = normalizeApiPayload(dataRaw);

  const msgs = [];

  if (typeof parsed === "string") {
    msgs.push(...stringBodyToUserMessages(parsed));
  } else {
    // 1) Direct list payload
    msgs.push(...messagesFromList(parsed));

    // 2) Common object wrappers
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      msgs.push(...messagesFromList(parsed.errores));
      msgs.push(...messagesFromList(parsed.errors ?? parsed.Errors));

      // ModelState: { errors: { field: [...] } }
      if (parsed.errors && !Array.isArray(parsed.errors)) {
        msgs.push(...messagesFromModelStateErrors(parsed.errors));
      }

      pushIfMsg(msgs, parsed.mensajen ?? parsed.mensaje ?? parsed.message ?? parsed.Message);
    }
  }

  // 3) Fallback (status / generic)
  if (msgs.length === 0) {
    const status = err?.response?.status;
    const statusText = err?.response?.statusText;
    if (status) pushIfMsg(msgs, `Error ${status}${statusText ? `: ${statusText}` : ""} (sin detalle en la respuesta).`);
    else pushIfMsg(msgs, err?.message || "Ocurrió un error al procesar la solicitud.");
  }

  // Dedupe (preserve order)
  return msgs.filter((m, i) => msgs.indexOf(m) === i);
}

export function toastApiErrors(err, toast, options) {
  const { fallbackMessage, level } = options || {};
  const toastLevel = level === "warn" ? "warn" : "error";

  const msgs = extractApiErrorMessages(err);
  const hasRealMsgs = msgs.length > 0;

  if (hasRealMsgs) {
    msgs.forEach((m) => toast?.[toastLevel]?.(m));
    return true;
  }

  if (fallbackMessage) toast?.[toastLevel]?.(fallbackMessage);
  return false;
}

